import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { useAnchoredPosition } from "hooks/useAnchoredPosition";
import type { DropdownProps, MenuPosition } from "./types";

const MENU_MAX_HEIGHT = 240; // matches max-h-60
const VIEWPORT_MARGIN = 8; // gap kept between the menu and the viewport edge

export const Dropdown = <T extends string>({
  options,
  value,
  defaultValue,
  onChange,
  placeholder = "Select...",
  startAdornment,
  className,
  buttonClassName,
  menuClassName,
  disabled = false,
}: DropdownProps<T>) => {
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<T | undefined>(
    defaultValue,
  );

  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedValue = isControlled ? value : internalValue;

  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectedValue),
    [options, selectedValue],
  );

  const menuPosition = useAnchoredPosition<MenuPosition>({
    isOpen,
    anchorRef: buttonRef,
    compute: (rect) => {
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement = spaceBelow < MENU_MAX_HEIGHT ? "top" : "bottom";
      return {
        top:
          placement === "top"
            ? rect.top + window.scrollY
            : rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        // At least the trigger width; never wider than the space to the viewport
        // edge, so a long-content menu grows but stays on screen.
        minWidth: rect.width,
        maxWidth: Math.max(
          rect.width,
          window.innerWidth - rect.left - VIEWPORT_MARGIN,
        ),
        placement,
      };
    },
  });

  const handleSelect = (nextValue: T) => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }
    onChange?.(nextValue);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const isClickInButton = containerRef.current?.contains(
        event.target as Node,
      );
      const isClickInMenu = menuRef.current?.contains(event.target as Node);
      if (!isClickInButton && !isClickInMenu) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={clsx("relative inline-block text-left", className)}
    >
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className={clsx(
          "flex items-center gap-2 text-sm text-gray-900 transition",
          "px-2 py-1 rounded-md",
          "border border-gray-200 bg-white",
          "focus-within:border-blue-400 focus:border-blue-400 focus:outline-none",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          buttonClassName,
        )}
      >
        {startAdornment}
        <span className="truncate">
          {selectedOption?.triggerLabel ?? selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          size={16}
          className={clsx(
            "text-gray-400 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>
      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className={clsx(
              "absolute z-50 rounded-md border border-gray-200 bg-white shadow-lg",
              "max-h-60 overflow-auto",
              menuClassName,
            )}
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: "max-content",
              minWidth: `${menuPosition.minWidth}px`,
              maxWidth: `${menuPosition.maxWidth}px`,
              transform:
                menuPosition.placement === "top"
                  ? "translateY(-100%)"
                  : undefined,
            }}
            onMouseDown={(event) => event.preventDefault()}
          >
            {options.map((option) => {
              const isSelected = option.value === selectedValue;
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => handleSelect(option.value)}
                  className={clsx(
                    // min-w-full fills the row without ballooning max-content
                    // (a percentage min-width is ignored when sizing the menu).
                    "flex min-w-full items-center gap-2 text-left px-3 py-2 text-sm transition",
                    "border-l-4 border-l-transparent",
                    option.disabled && "opacity-50 cursor-not-allowed",
                    isSelected
                      ? "bg-blue-100 border-l-blue-600 text-blue-700 font-medium"
                      : "hover:bg-gray-50",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};
